/**
 * 费用统计页面 — ExpenseStats（二期批3 翻新，对齐概念稿 expenses.html）。
 *
 * GET /api/expenses/stats?month&product_id   费用汇总（总/已确认/待确认/按产品/按媒体）
 * GET /api/delivery-plans?product_id         费用明细（投放计划即费用行）
 *
 * 结构：4 指标卡 → 月度费用趋势（近 4 个月，复用 month 参数逐月取真实值）→
 *       按产品归因 + 按媒体分布（cmp bars）→ 费用明细表 → 口径说明。
 * 选择器产品过滤生效（R1）：stats 与明细均带 product_id。
 *
 * 概念稿裁剪：篇数/篇均成本无接口字段（费用按投放计划建模，不按篇），跳过不虚构；
 * 「导出账单」按钮无后端端点，跳过；月度趋势各月数值为 stats 端点真实返回。
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
} from "@mui/material";
import PageHeader from "../components/PageHeader";
import StatCard from "../components/StatCard";
import DataTable, { type DataTableColumn } from "../components/DataTable";
import Badge from "../components/Badge";
import Loading from "../components/Loading";
import EmptyState from "../components/EmptyState";
import { getExpenseStats } from "../api/expenses";
import { getDeliveryPlans } from "../api/delivery";
import { useCompanyStore } from "../store/companyStore";
import { DELIVERY_STATUS_MAP } from "../utils/constants";
import { formatCurrency, formatDate } from "../utils/format";
import type { DeliveryPlan, ExpenseStats as ExpenseStatsData } from "../types";

/** 横向占比条（对齐概念稿 cmp-row）。 */
function CmpRow({
  label,
  valueText,
  ratio,
  color = "primary.main",
  hint,
}: {
  label: string;
  valueText: string;
  ratio: number;
  color?: string;
  hint?: string;
}) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
      <Typography sx={{ fontSize: 12, minWidth: 120, color: "text.secondary" }}>
        {label}
      </Typography>
      <Box
        sx={{
          flexGrow: 1,
          height: 6,
          borderRadius: 3,
          bgcolor: "semanticBg.neutral",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: `${Math.min(Math.max(ratio, 0), 1) * 100}%`,
            height: "100%",
            bgcolor: color,
            borderRadius: 3,
          }}
        />
      </Box>
      <Typography sx={{ fontSize: 12, fontWeight: 600, minWidth: 72, textAlign: "right" }}>
        {valueText}
      </Typography>
      {hint && (
        <Typography sx={{ fontSize: 11, color: "text.disabled", minWidth: 40 }}>
          {hint}
        </Typography>
      )}
    </Box>
  );
}

/** 近 N 个月（含本月）的 YYYY-MM 与标签。 */
function recentMonths(n: number): { month: string; label: string }[] {
  const now = new Date();
  const arr: { month: string; label: string }[] = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const mm = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    arr.push({
      month: mm,
      label: i === 0 ? `${d.getMonth() + 1} 月（本月）` : `${d.getMonth() + 1} 月`,
    });
  }
  return arr;
}

export default function ExpenseStats() {
  const selectedProductId = useCompanyStore((s) => s.selectedProductId);

  const [data, setData] = useState<ExpenseStatsData | null>(null);
  const [trend, setTrend] = useState<{ month: string; label: string; total: number }[]>([]);
  const [plans, setPlans] = useState<DeliveryPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  /** 汇总 + 趋势 + 明细（选择器产品过滤生效）。 */
  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const pid = selectedProductId || undefined;
      const months = recentMonths(4);
      const [stats, monthly, planList] = await Promise.all([
        getExpenseStats({ product_id: pid }),
        Promise.all(
          months.map((m) =>
            getExpenseStats({ month: m.month, product_id: pid }).catch(() => null),
          ),
        ),
        getDeliveryPlans({ product_id: pid }).catch(() => [] as DeliveryPlan[]),
      ]);
      setData(stats);
      setTrend(
        months.map((m, i) => ({
          ...m,
          total: monthly[i]?.total ?? 0,
        })),
      );
      setPlans(planList);
    } catch (err) {
      setError(err instanceof Error ? err.message : "获取费用统计失败");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedProductId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  /** 按产品归因最大值（占比条分母）。 */
  const maxProduct = useMemo(
    () => Math.max(1, ...(data?.by_product.map((p) => p.amount) || [1])),
    [data],
  );
  /** 按媒体分布最大值。 */
  const maxMedia = useMemo(
    () => Math.max(1, ...(data?.by_media.map((m) => m.amount) || [1])),
    [data],
  );
  /** 趋势最大值。 */
  const maxTrend = useMemo(
    () => Math.max(1, ...trend.map((t) => t.total)),
    [trend],
  );

  const columns: DataTableColumn<DeliveryPlan>[] = [
    {
      key: "date",
      label: "计划日期",
      render: (row) => (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {formatDate(row.planned_date)}
        </Typography>
      ),
    },
    {
      key: "media",
      label: "投放计划 / 媒体",
      render: (row) => (
        <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
          {row.media_name}
        </Typography>
      ),
    },
    {
      key: "product",
      label: "产品",
      render: (row) => (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {row.product_name_snapshot ||
            (row.product_name && row.product_name !== "未知产品"
              ? row.product_name
              : "（已删除产品）")}
        </Typography>
      ),
    },
    {
      key: "est",
      label: "预估费用",
      align: "right",
      render: (row) => (
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
          {formatCurrency(row.estimated_cost)}
        </Typography>
      ),
    },
    {
      key: "actual",
      label: "实际费用",
      align: "right",
      render: (row) => (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {row.actual_cost != null ? formatCurrency(row.actual_cost) : "—"}
        </Typography>
      ),
    },
    {
      key: "status",
      label: "状态",
      align: "center",
      render: (row) => {
        const info = DELIVERY_STATUS_MAP[row.status] || {
          label: row.status,
          color: "text.secondary",
          bg: "semanticBg.neutral",
        };
        return <Badge label={info.label} color={info.color} bgColor={info.bg} />;
      },
    },
  ];

  if (loading) {
    return (
      <Box>
        <PageHeader title="费用统计" />
        <Loading fullPage />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box>
        <PageHeader title="费用统计" />
        <Card>
          <CardContent>
            <EmptyState text={error || "暂无数据"} />
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="费用统计"
        breadcrumbs={[{ label: "媒体投放" }, { label: "费用统计" }]}
        subtitle="投放费用按产品归因快照归集 · 实际口径以投放计划登记为准"
        actions={
          data.pending > 0 ? (
            <Badge
              label={`待确认 ${formatCurrency(data.pending)}`}
              variant="warning"
              size="medium"
            />
          ) : undefined
        }
      />

      {/* 4 指标卡 */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="总费用（预估口径）"
            value={formatCurrency(data.total)}
            subtitle="当前选择器范围"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="已确认"
            value={formatCurrency(data.confirmed)}
            subtitle="已确认 + 已投放计划预估合计"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="待确认"
            value={formatCurrency(data.pending)}
            subtitle="待确认计划预估合计"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="覆盖产品"
            value={data.by_product.length}
            subtitle={`覆盖媒体 ${data.by_media.length} 个`}
          />
        </Grid>
      </Grid>

      {/* 月度费用趋势 */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Typography variant="h4">月度费用趋势</Typography>
            <Badge label="近 4 个月" variant="info" />
          </Box>
          {trend.every((t) => t.total === 0) ? (
            <EmptyState text="近 4 个月暂无费用记录" />
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-end",
                gap: 3,
                px: 2,
                pt: 1,
                pb: 0.5,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              {trend.map((t, i) => (
                <Box
                  key={t.month}
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: i === trend.length - 1 ? 700 : 500,
                      color: i === trend.length - 1 ? "text.primary" : "text.secondary",
                    }}
                  >
                    {t.total > 0 ? formatCurrency(t.total) : "—"}
                  </Typography>
                  <Box
                    sx={{
                      width: "60%",
                      maxWidth: 64,
                      height: 8 + (t.total / maxTrend) * 112,
                      borderRadius: 1,
                      bgcolor: "primary.main",
                      opacity: 0.35 + (0.65 * (i + 1)) / trend.length,
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: i === trend.length - 1 ? "text.primary" : "text.disabled",
                      fontWeight: i === trend.length - 1 ? 600 : 400,
                    }}
                  >
                    {t.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
          <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 1.5, lineHeight: 1.8 }}>
            各月数值为费用统计端点按 month 参数实查（预估口径）；本月数据随投放计划
            确认/登记实时变动。
          </Typography>
        </CardContent>
      </Card>

      {/* 按产品归因 + 按媒体分布 */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <Typography variant="h4">按产品归因</Typography>
                <Badge label="快照口径" />
              </Box>
              {data.by_product.length === 0 ? (
                <EmptyState text="暂无数据" />
              ) : (
                <>
                  {data.by_product.map((item, i) => (
                    <CmpRow
                      key={i}
                      label={
                        item.product_name_snapshot ||
                        (item.name && item.name !== "未知产品"
                          ? item.name
                          : "（已删除产品）")
                      }
                      valueText={formatCurrency(item.amount)}
                      ratio={item.amount / maxProduct}
                    />
                  ))}
                  <Box sx={{ borderTop: "1px solid", borderColor: "lineSoft", mt: 1, pt: 1 }}>
                    <CmpRow
                      label="已确认"
                      valueText={formatCurrency(data.confirmed)}
                      ratio={data.total > 0 ? data.confirmed / data.total : 0}
                      color="success.main"
                    />
                    <CmpRow
                      label="待确认"
                      valueText={formatCurrency(data.pending)}
                      ratio={data.total > 0 ? data.pending / data.total : 0}
                      color="warning.main"
                    />
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <Typography variant="h4">按媒体分布</Typography>
                <Badge label={`${data.by_media.length} 个媒体`} variant="info" />
              </Box>
              {data.by_media.length === 0 ? (
                <EmptyState text="暂无数据" />
              ) : (
                data.by_media.map((item, i) => (
                  <CmpRow
                    key={i}
                    label={item.name}
                    valueText={formatCurrency(item.amount)}
                    ratio={item.amount / maxMedia}
                  />
                ))
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 费用明细表 */}
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Typography variant="h4">费用明细</Typography>
          <Badge label={`${plans.length} 笔投放计划`} />
        </Box>
        <DataTable
          columns={columns}
          data={plans}
          showPagination={false}
          getRowId={(row) => row.id}
          emptyText="暂无费用明细（投放计划）"
        />
      </Box>

      {/* 口径说明 */}
      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Typography sx={{ fontSize: 12, color: "text.secondary", lineHeight: 1.8 }}>
            合计 {plans.length} 笔 / 预估 {formatCurrency(data.total)}（已确认{" "}
            {formatCurrency(data.confirmed)} + 待确认 {formatCurrency(data.pending)}）。
            费用行即投放计划：预估费用建档时登记，实际费用在投放计划页「登记完成」回填；
            产品归因以投放时快照为准，删除产品不影响历史归因；单篇效果可追溯至「发文记录」。
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
